import React, { useCallback, useEffect, useRef, useState } from 'react';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import clsx from 'clsx';
import ClickAwayListener from '@mui/material/ClickAwayListener';

import Icon from '../../icons/Icon';
import { ICONS } from '../../icons';
import { COMMON_POPPER_MODIFIERS } from '../../constants/popper';

import './CommonPopper.scss';

const CommonPopper = ({
  component: C,
  children,
  options = COMMON_POPPER_MODIFIERS,
  className = '',
  isShowCaret = true,
  onOpen,
  onClose,
  placement = 'bottom',
  anchorEl: anchorElProps = null,
  triggerOnHover = false,
  disablePortal = true,
  disabled,
  anchorElWidth = false,
  maxWidth,
  isOpen,
  zIndex = 9998,
}) => {
  const [anchorEl, setAnchorEl] = useState(anchorElProps);
  const buttonRef = useRef(null);
  const open = Boolean(anchorEl);

  const id = open ? 'button-popper' : undefined;

  const handleOpen = useCallback((event) => {
    setAnchorEl((prevAnchorEl) => (prevAnchorEl ? null : event.currentTarget));
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    setAnchorEl(anchorElProps);
  }, [anchorElProps]);

  useEffect(() => {
    if (anchorEl && onOpen) {
      onOpen();
    }

    if (!anchorEl && onClose) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEl]);

  useEffect(() => {
    if (!isOpen) {
      handleClose();
    }
  }, [isOpen, handleClose]);

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div className={className}>
        <button
          disabled={disabled}
          className={clsx('button-popper', open && '__active')}
          aria-describedby={id}
          onClick={!triggerOnHover ? handleOpen : () => null}
          onMouseEnter={triggerOnHover ? handleOpen : () => null}
          ref={buttonRef}>
          {children}
          {isShowCaret ? (
            <Icon
              icon={ICONS.CaretUp}
              disableCommonStyles
              className={clsx('button-popper_icon', open ? 'button-popper_icon--up' : 'button-popper_icon--down')}
            />
          ) : null}
        </button>

        <Popper
          style={{ zIndex, width: maxWidth || anchorElWidth ? buttonRef.current?.clientWidth || '' : '' }}
          id={id}
          open={open}
          anchorEl={anchorEl}
          disablePortal={disablePortal}
          transition
          placement={placement}
          modifiers={options}>
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Box sx={boxStyles}>
                <C onClose={handleClose} />
              </Box>
            </Fade>
          )}
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

export default CommonPopper;

const boxStyles = {
  p: '0px',
  bgcolor: 'transparent',
  boxShadow: 'none',
  width: '100%',
};
